import { Edge, Node } from "@xyflow/react";
import { SequenceNodeData } from "./SequenceNode";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import { ISubSequence } from "../../models/interactions/SubSequence";
import { TAKE3D_ON_END_DEFAULT_POSITION } from "../../models/interactions/SequenceModel";
import {
  ITrigger,
  TriggerType,
  getTriggerLabel,
} from "../../models/interactions/Trigger";

export function isTakeSequenceEditor(selectedElementType?: string) {
  return selectedElementType === "takeSequence";
}

const isTakeTrigger = (node: Node<SequenceNodeData>, triggerType: TriggerType) =>
  node.data?.interaction?.type === InteractionType.Trigger &&
  (node.data.interaction as ITrigger).triggerType === triggerType;

/** Room between On Take Start and an On Take End drawn below it. */
const TAKE_END_GAP = 48;

/**
 * On Take Start grows with the take's setup, so an On Take End still at the
 * spot new takes give it is drawn below it instead of underneath it. Only
 * drawn there: dragging On Take End saves wherever it is dropped.
 */
export function placeDefaultTakeEnd<T extends Node<SequenceNodeData>>(
  nodes: T[],
  heightOf: (node: T) => number | undefined,
): T[] {
  const start = nodes.find((node) => isTakeTrigger(node, TriggerType.OnStart));
  const end = nodes.find((node) => isTakeTrigger(node, TriggerType.OnEnd));
  if (!start || !end) return nodes;
  const { x, y } = TAKE3D_ON_END_DEFAULT_POSITION;
  if (end.position.x !== x || end.position.y !== y) return nodes;

  const height = heightOf(start);
  if (!height) return nodes;
  const below = start.position.y + height + TAKE_END_GAP;
  if (below <= y) return nodes;
  return nodes.map((node) =>
    node === end ? { ...node, position: { x, y: below } } : node,
  );
}

export function isProtectedSequenceStructureInteraction(
  interaction: Interaction,
  selectedElementType?: string,
) {
  if (interaction.type === InteractionType.Sequence) return true;
  if (
    !isTakeSequenceEditor(selectedElementType) ||
    interaction.type !== InteractionType.Trigger
  ) {
    return false;
  }

  const triggerType = (interaction as ITrigger).triggerType;
  return (
    triggerType === TriggerType.OnStart || triggerType === TriggerType.OnEnd
  );
}

export function isSequenceRootNode(node: Node<SequenceNodeData>) {
  return node.data?.interaction?.type === InteractionType.Sequence;
}

export function isTakeSequenceGroupNode(node: Node<SequenceNodeData>) {
  const type = node.data?.interaction?.type;
  return (
    type === InteractionType.SubSequence || type === InteractionType.Trigger
  );
}

export function isProtectedTakeSequenceTrigger(
  node: Node<SequenceNodeData>,
  selectedElementType?: string,
) {
  if (!node.data?.interaction) return false;
  return isProtectedSequenceStructureInteraction(
    node.data.interaction,
    selectedElementType,
  );
}

export function getTakeSequenceGroupLabel(node: Node<SequenceNodeData>) {
  if (node.data?.interaction?.type === InteractionType.Trigger) {
    return getTriggerLabel((node.data.interaction as ITrigger).triggerType);
  }

  if (node.data?.interaction?.type === InteractionType.SubSequence) {
    return (node.data.interaction as ISubSequence).sequenceName;
  }

  return node.data?.name ?? node.data?.interaction?.name ?? node.id;
}

export function getVisibleSequenceNodes(
  nodes: Node<SequenceNodeData>[],
  selectedElementType?: string,
) {
  if (!isTakeSequenceEditor(selectedElementType)) {
    return nodes;
  }

  return nodes.filter((node) => !isSequenceRootNode(node));
}

export function getVisibleSequenceEdges(
  nodes: Node<SequenceNodeData>[],
  edges: Edge[],
  selectedElementType?: string,
) {
  const visibleNodeIds = new Set(
    getVisibleSequenceNodes(nodes, selectedElementType).map((node) => node.id),
  );

  return edges.filter(
    (edge) =>
      visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target),
  );
}
